import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getApi } from '../../../Services/apiService';

const PetDetail = () => {
    const { petId } = useParams();
    const navigate = useNavigate();
    const [pet, setPet] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchPet = async () => {
            if (!petId) return;
            setLoading(true);
            setError(null);
            try {
                const response = await getApi(`/api/v1/admin/pets/${petId}`);
                const data = response?.data ?? response;
                setPet(data);
            } catch (err) {
                console.error('Error fetching pet details:', err);
                setError('Failed to load pet details.');
                setPet(null);
            } finally {
                setLoading(false);
            }
        };
        fetchPet();
    }, [petId]);

    const getStatusPillClass = (status) => {
        const s = (status || '').toLowerCase();
        if (s === 'seen') return 'seen';
        if (s === 'lost') return 'lost';
        if (s === 'found') return 'found';
        return 'inactive';
    };

    const displayStatus = (status) => {
        const s = (status || '').toLowerCase();
        if (s === 'seen') return 'Seen';
        if (s === 'lost') return 'Lost';
        if (s === 'found') return 'Found';
        return status || '-';
    };

    if (loading) {
        return (
            <div className="RightSideDiv">
                <div style={{ padding: '60px 20px', textAlign: 'center' }}>
                    <p>Loading pet details...</p>
                </div>
            </div>
        );
    }

    if (error || !pet) {
        return (
            <div className="RightSideDiv">
                <div style={{ padding: '60px 20px', textAlign: 'center' }}>
                    <p>{error || 'Pet not found.'}</p>
                    <button type="button" className="add-shelter-btn" style={{ marginTop: 16 }} onClick={() => navigate('/admin/pet-management')}>
                        Back to Pet management
                    </button>
                </div>
            </div>
        );
    }

    const name = pet.petName ?? pet.name ?? 'Pet';
    const imageSrc = pet.image ?? pet.photo ?? pet.profileImage ?? pet.petImage;

    const owner = pet.owner ?? pet.user ?? {};
    const ownerName = typeof owner === 'object' ? (owner.name ?? owner.fullName ?? owner.ownerName ?? '-') : String(owner || '-');
    const ownerPhone = typeof owner === 'object' ? (owner.phone ?? owner.mobile ?? owner.phoneNumber ?? '-') : '-';
    const ownerEmail = typeof owner === 'object' ? (owner.email ?? '-') : '-';
    const ownerId = (typeof owner === 'object' && (owner.id ?? owner.userId ?? owner._id)) ?? pet.userId ?? pet.ownerId ?? pet.owner_id ?? null;

    const overviewAttributes = [
        { label: 'Gender', value: pet.gender },
        { label: 'Species', value: pet.species },
        { label: 'Breed', value: pet.breed },
        { label: 'Age', value: pet.age ? (String(pet.age).includes('yr') ? pet.age : `${pet.age} yrs old`) : null },
        { label: 'Weight', value: pet.weight ? `${pet.weight} kg` : null },
    ];

    const identificationAttributes = [
        { label: 'Color', value: pet.color },
        { label: 'Personality', value: pet.personality },
        { label: 'Marking/Pattern', value: pet.markingPattern ?? pet.marking ?? pet.pattern },
        { label: 'Microchip number', value: pet.microchipNumber ?? pet.microchip },
        { label: 'Is pet sterilized', value: pet.isSterilized != null ? (pet.isSterilized ? 'Yes' : 'No') : (pet.sterilized != null ? (pet.sterilized ? 'Yes' : 'No') : null) },
        { label: 'Activity level', value: pet.activityLevel ?? pet.activity },
        { label: 'Coat type', value: pet.coatType ?? pet.coat },
        { label: 'Lifestyle', value: pet.lifestyle },
    ];

    return (
        <div className="RightSideDiv">
            <div className="pet-detail-layout">
                <div className="pet-detail-card pet-detail-overview-card">
                    <div className="pet-detail-image-wrap">
                        <img
                            src={imageSrc?.startsWith?.('http') ? imageSrc : (imageSrc ? window.getPublicUrl(imageSrc) : window.getPublicUrl('/assets/images/dummy-profile.jpg'))}
                            alt={name}
                            className="pet-detail-image"
                            onError={(e) => { e.target.src = window.getPublicUrl('/assets/images/dummy-profile.jpg'); }}
                        />
                    </div>
                    <div className="pet-detail-overview-content">
                        <div className="pet-detail-header">
                            <h2 className="pet-detail-name">{name}</h2>
                            <span className={`status-badge status-badge-pet ${getStatusPillClass(pet.status)}`}>
                                {displayStatus(pet.status)}
                            </span>
                        </div>
                        <div className="pet-detail-attributes">
                            {overviewAttributes.map(({ label, value }) => (
                                <div key={label} className="pet-detail-attr-row">
                                    <span className="pet-detail-attr-label">{label}</span>
                                    <span className="pet-detail-attr-value">{value ?? '-'}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="pet-detail-cards-row">
                    <div className="pet-detail-card pet-detail-subcard">
                        <h3 className="pet-detail-subcard-title">Identification and Lifestyle</h3>
                        <div className="pet-detail-attributes">
                            {identificationAttributes.map(({ label, value }) => (
                                <div key={label} className="pet-detail-attr-row">
                                    <span className="pet-detail-attr-label">{label}</span>
                                    <span className="pet-detail-attr-value">{value ?? '-'}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="pet-detail-card pet-detail-subcard pet-detail-owner-card">
                        <h3 className="pet-detail-subcard-title">Owner Contact</h3>
                        <div className="pet-detail-attributes">
                            <div className="pet-detail-attr-row">
                                <span className="pet-detail-attr-label">Name</span>
                                <span className="pet-detail-attr-value">{ownerName}</span>
                            </div>
                            <div className="pet-detail-attr-row">
                                <span className="pet-detail-attr-label">Phone number</span>
                                <span className="pet-detail-attr-value">{ownerPhone}</span>
                            </div>
                            <div className="pet-detail-attr-row">
                                <span className="pet-detail-attr-label">Email</span>
                                <span className="pet-detail-attr-value">{ownerEmail}</span>
                            </div>
                        </div>
                        {ownerId && (
                            <button
                                type="button"
                                className="add-shelter-btn pet-detail-view-user-btn"
                                onClick={() => navigate(`/admin/users/${ownerId}`)}
                            >
                                View user detail
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PetDetail;
